// ============================================================================
// 1. 임포트 및 전역 설정 (Imports & Config)
// ============================================================================
import React, { useState, useEffect, useMemo } from 'react';
import { 
  RefreshCw, Edit3, Database, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, 
  Download, Clock, Trash2, MapPin, Check, Search, X, Plus, ChevronLeft, ChevronRight 
} from 'lucide-react'; // 🚨 검색, 페이지네이션, 추가용 아이콘 추가
import { crmDataService } from '../../services/crmDataService';

export default function G2BAdminBoard() {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCell, setEditingCell] = useState<{bidNo: string, field: string} | null>(null);
  const [editValue, setEditValue] = useState("");
  
  // 🚨 최종 업데이트 시간 상태
  const [lastUpdated, setLastUpdated] = useState<string>("알 수 없음");
  
  const [statusFilter, setStatusFilter] = useState("전체");
  const [regionFilter, setRegionFilter] = useState("전체"); // 🚨 [추가] 지역 필터 상태
  const [searchTerm, setSearchTerm] = useState(""); // 🚨 [추가] 검색어 상태
  const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' } | null>(null);

  // 🚨 [추가] 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // 기본값 10개

  // 🚨 [추가] 공고 수동 추가 모달 상태
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newBid, setNewBid] = useState({
    bid_no: '', bid_name: '', instt_name: '', region: '미분류', status: '공고중', 
    budget: 0, base_price: 0, ntce_date: '', end_date: ''
  });

  const fetchBids = async () => {
    setLoading(true);
    try {
      const data = await crmDataService.getG2BList();
      setBids(data);
      
      // 데이터를 가져온 후, 현재 시간을 '최종 동기화 시간'으로 설정
      const now = new Date();
      const formattedTime = `${now.getFullYear()}.${String(now.getMonth() + 1).padStart(2, '0')}.${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      setLastUpdated(formattedTime);
      
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBids(); }, []);

  const openG2B = (bidNo: string) => {
    const [no, ord] = bidNo.split('-');
    window.open(`https://www.g2b.go.kr/pt/menu/selectSubMenu.do?menuId=PT0080&bidNtceNo=${no}&bidNtceOrd=${ord || '00'}`, '_blank');
  };

  // 🚨 [수정됨] 수동 입력 시 value를 직접 인자로 받을 수 있도록 개선
  const handleSave = async (bidNo: string, field: string, manualValue?: string) => {
    try {
      const valueToSave = manualValue !== undefined ? manualValue : editValue;
      const cleanValue = valueToSave.replace(/,/g, '');
      await crmDataService.updateG2BField(bidNo, field, cleanValue);
      setEditingCell(null);
      fetchBids(); 
    } catch (err) { alert("저장 실패"); }
  };

  // 🚨 [추가] 삭제 기능
  const handleDelete = async (bidNo: string) => {
    if (!window.confirm(`정말 공고번호 [${bidNo}] 데이터를 삭제하시겠습니까?`)) return;
    try {
      await crmDataService.deleteG2BData(bidNo); 
      alert("삭제되었습니다.");
      fetchBids();
    } catch (err) {
      alert("삭제 실패");
    }
  };

  // 🚨 [추가] 수동 공고 등록
  const handleAddBid = async () => {
    if (!newBid.bid_no || !newBid.bid_name) {
      alert("공고번호와 공고명은 필수입니다."); return;
    }
    try {
      // 💡 백엔드에 addG2BData API가 구현되어 있어야 합니다 (upsertG2B_data 등 활용)
      if(crmDataService.addG2BData) {
         await crmDataService.addG2BData(newBid);
      } else {
         // 임시 처리용 (백엔드 API 없을 시 경고)
         console.warn("addG2BData API가 서비스에 연결되지 않았습니다.");
         alert("수동 추가 API 연동이 필요합니다.");
      }
      alert("공고가 추가되었습니다.");
      setIsAddModalOpen(false);
      fetchBids();
    } catch (err) {
      alert("추가 중 오류가 발생했습니다.");
    }
  };

  // 🚨 [추가] 엑셀(CSV) 다운로드 기능
  const exportToExcel = () => {
    if (processedBids.length === 0) return alert("다운로드할 데이터가 없습니다.");

    // CSV 헤더
    const headers = ["세부품명번호", "입찰공고번호", "공고명", "발주처/수요기관", "상태", "배정예산", "기초금액", "낙찰금액", "투찰율", "낙찰업체", "게시일시", "마감일시", "지역"];
    
    // 데이터 추출
    const csvData = processedBids.map((bid: any) => [
      bid.item_no || "",
      bid.bid_no || "",
      `"${(bid.bid_name || "").replace(/"/g, '""')}"`,
      `"${(bid.instt_name || "").replace(/"/g, '""')}"`,
      bid.status || "",
      bid.budget || 0,
      bid.base_price || 0,
      bid.win_price || 0,
      bid.win_rate || 0,
      `"${(bid.win_company || "").replace(/"/g, '""')}"`, 
      bid.ntce_date || "",
      bid.end_date || "",
      bid.region || ""
    ]);

    const csvContent = "\uFEFF" + [headers.join(","), ...csvData.map(row => row.join(","))].join("\n");
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `나라장터_수집데이터_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSort = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      setSortConfig({ key, direction: 'asc' });
    } else if (sortConfig.direction === 'asc') {
      setSortConfig({ key, direction: 'desc' });
    } else {
      setSortConfig(null);
    }
  };

  // 🚨 [추가] 고유 지역 목록 추출
  const uniqueRegions = useMemo(() => {
    const regions = new Set(bids.map((b: any) => b.region).filter(Boolean));
    return ["전체", ...Array.from(regions).sort()];
  }, [bids]);

  const processedBids = useMemo(() => {
    // 🚨 [수정됨] 상태 필터 + 지역 필터 + 검색 필터 동시 적용
    let filterableBids = bids.filter((bid: any) => {
      const matchStatus = statusFilter === "전체" || bid.status === statusFilter;
      const matchRegion = regionFilter === "전체" || bid.region === regionFilter;
      
      const searchLower = searchTerm.toLowerCase();
      const matchSearch = !searchTerm || 
        (bid.bid_name?.toLowerCase().includes(searchLower)) ||
        (bid.instt_name?.toLowerCase().includes(searchLower)) ||
        (bid.bid_no?.toLowerCase().includes(searchLower)) ||
        (bid.win_company?.toLowerCase().includes(searchLower));

      return matchStatus && matchRegion && matchSearch;
    });

    if (sortConfig !== null) {
      filterableBids = [...filterableBids].sort((a: any, b: any) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        const strA = String(aValue).toLowerCase();
        const strB = String(bValue).toLowerCase();
        if (strA < strB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (strA > strB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filterableBids;
  }, [bids, statusFilter, regionFilter, sortConfig, searchTerm]);

  // 🚨 [추가] 필터나 개수가 바뀌면 무조건 1페이지로 돌아가기
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, regionFilter, searchTerm, sortConfig, itemsPerPage]);

  // 🚨 [추가] 화면에 그릴 페이지 단위 데이터 계산
  const totalPages = Math.ceil(processedBids.length / itemsPerPage) || 1;
  const paginatedBids = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return processedBids.slice(start, start + itemsPerPage);
  }, [processedBids, currentPage, itemsPerPage]);

  const statusCounts = {
    "전체": bids.length,
    "공고중": bids.filter((bid: any) => bid.status === '공고중').length,
    "입찰마감(유찰의심)": bids.filter((bid: any) => bid.status === '입찰마감(유찰의심)').length,
    "낙찰완료": bids.filter((bid: any) => bid.status === '낙찰완료').length,
    "입찰마감": bids.filter((bid: any) => bid.status === '입찰마감').length,
    "입찰전": bids.filter((bid: any) => bid.status === '입찰전').length,
  };

  const SortIcon = ({ columnKey }: { columnKey: string }) => {
    if (sortConfig?.key !== columnKey) return <ArrowUpDown size={12} className="inline ml-1 opacity-20 group-hover:opacity-100 transition-opacity" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp size={12} className="inline ml-1 text-blue-500" />
      : <ArrowDown size={12} className="inline ml-1 text-blue-500" />;
  };

  // 🚨 [추가] 날짜에서 'T' 제거하고 예쁘게 포맷팅하는 함수
  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '-';
    // '2026-03-26T14:30' -> '26-03-26 14:30'
    return dateStr.replace('T', ' ').substring(2, 16);
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden font-sans">
      
      {/* 헤더 */}
      <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Database size={18} className="text-blue-600" />
            <h2 className="font-black text-slate-800 uppercase tracking-tight">나라장터 입찰공고</h2>
          </div>
          
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-md shadow-sm">
            <Clock size={12} className="text-slate-400" />
            <span className="text-[10px] font-bold text-slate-500">
              최종 동기화: <span className="text-blue-600">{lastUpdated}</span>
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 🚨 [추가] 통합 검색창 */}
          <div className="flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 rounded-lg shadow-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-200 transition-all">
            <Search size={14} className="text-slate-400" />
            <input 
              type="text" 
              placeholder="공고명, 수요기관, 업체명..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="text-[11px] font-black text-slate-700 outline-none w-[160px] bg-transparent placeholder:text-slate-300 placeholder:font-normal"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="text-slate-300 hover:text-rose-500 transition-colors">
                <X size={12} strokeWidth={3} />
              </button>
            )}
          </div>

          {/* 🚨 [기존] 지역 필터 드롭다운 디자인 */}
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-lg shadow-sm mr-2">
             <MapPin size={14} className="text-slate-400" />
             <select 
               value={regionFilter} 
               onChange={(e) => setRegionFilter(e.target.value)}
               className="text-[11px] font-black text-slate-600 outline-none bg-transparent cursor-pointer"
             >
               {uniqueRegions.map(reg => <option key={reg} value={reg}>{reg}</option>)}
             </select>
          </div>

          {/* 🚨 [추가] 수동 추가 버튼 */}
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-[11px] font-black border border-blue-700 transition-all shadow-sm"
          >
            <Plus size={14} strokeWidth={2.5} /> 공고 추가
          </button>

          <button 
            onClick={exportToExcel} 
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg text-[11px] font-black border border-emerald-200 transition-all shadow-sm"
          >
            <Download size={14} strokeWidth={2.5} /> 엑셀 다운로드
          </button>
          
          <button onClick={fetchBids} className="p-2 hover:bg-white rounded-lg border border-slate-200 transition-all active:scale-95 shadow-sm">
            <RefreshCw size={16} className={loading ? 'animate-spin text-blue-500' : 'text-slate-400'}/>
          </button>
        </div>
      </div>

      <div className="p-3 bg-white border-b border-slate-200 flex justify-between items-center overflow-x-auto">
        <div className="flex gap-2 items-center">
          {Object.entries(statusCounts).map(([status, count]) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-1.5 text-xs font-bold rounded-full transition-colors whitespace-nowrap shadow-sm ${
                statusFilter === status 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {status} <span className="ml-1 opacity-80 font-normal">({count})</span>
            </button>
          ))}
        </div>
        
        {/* 🚨 [추가] 페이지네이션 갯수 선택 */}
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold text-slate-500">페이지 당:</span>
          <select 
            value={itemsPerPage} 
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="text-[11px] font-black border border-slate-200 rounded px-2 py-1 outline-none focus:border-blue-500"
          >
            <option value={10}>10개</option>
            <option value={50}>50개</option>
            <option value={100}>100개</option>
          </select>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-[#f8fafc]">
        <table className="w-full border-collapse text-[11px] whitespace-nowrap">
          <thead className="sticky top-0 z-20 shadow-sm">
            <tr className="bg-slate-800 text-slate-300 font-bold divide-x divide-slate-700 select-none">
              <th className="p-2.5 text-blue-300 cursor-pointer hover:bg-slate-700 transition-colors group" onClick={() => handleSort('item_no')}>
                세부품명번호 <SortIcon columnKey="item_no" />
              </th>
              <th className="p-2.5 text-blue-300 cursor-pointer hover:bg-slate-700 transition-colors group" onClick={() => handleSort('bid_no')}>
                입찰공고번호 <SortIcon columnKey="bid_no" />
              </th>
              <th className="p-2.5 text-left min-w-[200px] cursor-pointer hover:bg-slate-700 transition-colors group" onClick={() => handleSort('bid_name')}>
                공고명 <SortIcon columnKey="bid_name" />
              </th>
              <th className="p-2.5 text-left cursor-pointer hover:bg-slate-700 transition-colors group" onClick={() => handleSort('instt_name')}>
                발주처/수요기관 <SortIcon columnKey="instt_name" />
              </th>
              <th className="p-2.5 text-center cursor-pointer hover:bg-slate-700 transition-colors group" onClick={() => handleSort('status')}>
                상태 <SortIcon columnKey="status" />
              </th>
              <th className="p-2.5 text-rose-400 font-black cursor-pointer hover:bg-slate-700 transition-colors group" onClick={() => handleSort('budget')}>
                배정예산 <SortIcon columnKey="budget" />
              </th>
              <th className="p-2.5 text-rose-300 cursor-pointer hover:bg-slate-700 transition-colors group" onClick={() => handleSort('base_price')}>
                기초금액 <SortIcon columnKey="base_price" />
              </th>
              <th className="p-2.5 text-rose-400 font-black cursor-pointer hover:bg-slate-700 transition-colors group" onClick={() => handleSort('win_price')}>
                낙찰금액 <SortIcon columnKey="win_price" />
              </th>

              {/* 🚨 테이블 헤더: 낙찰업체 유지 */}
              <th className="p-2.5 text-amber-400 cursor-pointer hover:bg-slate-700 transition-colors group" onClick={() => handleSort('win_company')}>
                낙찰업체 <SortIcon columnKey="win_company" />
              </th>
              
              <th className="p-2.5 text-emerald-400 cursor-pointer hover:bg-slate-700 transition-colors group" onClick={() => handleSort('win_rate')}>
                투찰율 <SortIcon columnKey="win_rate" />
              </th>
              
              <th className="p-2.5 text-indigo-300 cursor-pointer hover:bg-slate-700 transition-colors group" onClick={() => handleSort('ntce_date')}>
                게시일시 <SortIcon columnKey="ntce_date" />
              </th>
              <th className="p-2.5 text-indigo-300 cursor-pointer hover:bg-slate-700 transition-colors group" onClick={() => handleSort('end_date')}>
                마감일시 <SortIcon columnKey="end_date" />
              </th>
              <th className="p-2.5 text-indigo-300 cursor-pointer hover:bg-slate-700 transition-colors group" onClick={() => handleSort('region')}>
                지역 <SortIcon columnKey="region" />
              </th>
              <th className="p-2.5 text-slate-400">관리</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {/* 🚨 [수정] processedBids 대신 페이지 처리된 paginatedBids 매핑 */}
            {paginatedBids.map((bid: any) => (
              <tr key={bid.id} className="bg-white hover:bg-slate-50/80 transition-colors group">
                
                <td className="p-2 text-center text-slate-400 bg-slate-50/50 font-mono border-r border-slate-100">{bid.item_no}</td>
                <td className="p-2 text-center font-black text-blue-600 bg-blue-50/30 cursor-pointer hover:underline border-r border-blue-100" onClick={() => openG2B(bid.bid_no)}>
                  {bid.bid_no} <ExternalLink size={10} className="inline opacity-50 ml-1" />
                </td>

                <EditableCell bid={bid} field="bid_name" value={bid.bid_name} onSave={handleSave} className="font-bold text-slate-800 px-3 max-w-[250px] truncate"
                  editingCell={editingCell} setEditingCell={setEditingCell} editValue={editValue} setEditValue={setEditValue} />
                <td className="p-2 text-slate-500 border-r border-slate-100 max-w-[150px] truncate" title={bid.instt_name}>{bid.instt_name}</td>

                {/* 🚨 상태 드롭다운 적용 */}
                <EditableCell 
                   bid={bid} field="status" value={bid.status} onSave={handleSave} 
                   className="text-center font-black border-r border-slate-100"
                   editingCell={editingCell} setEditingCell={setEditingCell} editValue={editValue} setEditValue={setEditValue} 
                   isSelect={true} options={["공고중", "입찰전", "입찰마감", "입찰마감(유찰의심)", "낙찰완료"]} 
                />

                <EditableCell bid={bid} field="budget" value={bid.budget?.toLocaleString()} onSave={handleSave} 
                  className="text-right font-black text-rose-600 bg-rose-50/30 border-r border-rose-100 px-3"
                  editingCell={editingCell} setEditingCell={setEditingCell} editValue={editValue} setEditValue={setEditValue} />
                
                <EditableCell bid={bid} field="base_price" value={bid.base_price?.toLocaleString()} onSave={handleSave} 
                  className="text-right text-rose-400 bg-rose-50/10 border-r border-rose-50 px-3"
                  editingCell={editingCell} setEditingCell={setEditingCell} editValue={editValue} setEditValue={setEditValue} />

                <EditableCell bid={bid} field="win_price" value={bid.win_price?.toLocaleString()} onSave={handleSave} 
                  className="text-right font-black text-rose-600 bg-rose-100/40 border-r border-rose-100 px-3"
                  editingCell={editingCell} setEditingCell={setEditingCell} editValue={editValue} setEditValue={setEditValue} />

                <EditableCell 
                   bid={bid} field="win_company" value={bid.win_company} onSave={handleSave} 
                   className="text-center font-black text-amber-700 bg-amber-50/30 border-r border-amber-100 truncate max-w-[120px]"
                   editingCell={editingCell} setEditingCell={setEditingCell} editValue={editValue} setEditValue={setEditValue} 
                   placeholder="업체명 입력"
                />

                <td className="p-2 text-center font-bold text-emerald-600 border-r border-slate-100">
                  {bid.win_rate ? `${bid.win_rate}%` : '-'}
                </td>

                {/* 🚨 [수정] 날짜 T 제거 적용 */}
                <td className="p-2 text-center text-indigo-400 bg-indigo-50/20 border-r border-indigo-50">{formatDateTime(bid.ntce_date)}</td>
                <td className="p-2 text-center text-indigo-600 font-bold bg-indigo-50/40 border-r border-indigo-100">{formatDateTime(bid.end_date)}</td>
                
                <EditableCell bid={bid} field="region" value={bid.region} onSave={handleSave} 
                  className="text-center text-indigo-800 font-black bg-indigo-100/30 border-r border-indigo-200"
                  editingCell={editingCell} setEditingCell={setEditingCell} editValue={editValue} setEditValue={setEditValue} />

                <td className="p-2 text-center">
                  <button onClick={() => handleDelete(bid.bid_no)} className="p-1 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors" title="이 공고 삭제">
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {paginatedBids.length === 0 && (
              <tr>
                <td colSpan={14} className="p-10 text-center text-slate-400 font-bold">
                  조건에 맞는 데이터가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 🚨 [추가] 하단 페이지네이션 컨트롤러 */}
      <div className="bg-white border-t border-slate-200 p-3 flex justify-between items-center">
        <span className="text-[11px] font-bold text-slate-500">
          총 <span className="text-blue-600">{processedBids.length}</span> 건 중 {currentPage} / {totalPages} 페이지
        </span>
        <div className="flex gap-1">
          <button 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30 transition-all"
          >
            <ChevronLeft size={16} className="text-slate-600" />
          </button>
          
          <div className="flex items-center gap-1 px-2">
            {/* 페이지 번호 (최대 5개 표시 등 로직 추가 가능, 현재는 심플하게 표시) */}
            <span className="text-[11px] font-black text-slate-700">{currentPage}</span>
          </div>

          <button 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-30 transition-all"
          >
            <ChevronRight size={16} className="text-slate-600" />
          </button>
        </div>
      </div>

      {/* 🚨 [추가] 수동 추가 모달 */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl w-[450px] overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
              <h3 className="font-black text-slate-800 flex items-center gap-2">
                <Plus size={16} className="text-blue-600" /> 공고 수동 추가
              </h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-slate-400 hover:text-red-500"><X size={18} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4 text-xs font-bold text-slate-600">
              <div className="col-span-2">
                <label className="block mb-1">공고번호 (필수)</label>
                <input value={newBid.bid_no} onChange={e => setNewBid({...newBid, bid_no: e.target.value})} className="w-full p-2 border rounded outline-none focus:border-blue-500" placeholder="예: 20261234567-00" />
              </div>
              <div className="col-span-2">
                <label className="block mb-1">공고명 (필수)</label>
                <input value={newBid.bid_name} onChange={e => setNewBid({...newBid, bid_name: e.target.value})} className="w-full p-2 border rounded outline-none focus:border-blue-500" placeholder="공고명 입력" />
              </div>
              <div className="col-span-2">
                <label className="block mb-1">발주처/수요기관</label>
                <input value={newBid.instt_name} onChange={e => setNewBid({...newBid, instt_name: e.target.value})} className="w-full p-2 border rounded outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block mb-1">배정예산</label>
                <input type="number" value={newBid.budget} onChange={e => setNewBid({...newBid, budget: Number(e.target.value)})} className="w-full p-2 border rounded outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block mb-1">기초금액</label>
                <input type="number" value={newBid.base_price} onChange={e => setNewBid({...newBid, base_price: Number(e.target.value)})} className="w-full p-2 border rounded outline-none focus:border-blue-500" />
              </div>
              <div>
                <label className="block mb-1">지역</label>
                <select value={newBid.region} onChange={e => setNewBid({...newBid, region: e.target.value})} className="w-full p-2 border rounded outline-none focus:border-blue-500">
                  <option value="미분류">미분류</option>
                  <option value="제주">제주</option>
                  <option value="경기">경기</option>
                  <option value="서울">서울</option>
                  <option value="부산">부산</option>
                  <option value="경북">경북</option>
                  <option value="경남">경남</option>
                  <option value="충북">충북</option>
                  <option value="전남">전남</option>
                  <option value="충남">충남</option>
                  <option value="울산">울산</option>
                  <option value="인천">인천</option>
                  <option value="강원">강원</option>
                  <option value="전북">전북</option>
                  <option value="대구">대구</option>
                  <option value="광주">광주</option>
                  <option value="대전">대전</option>
                  {/* 필요시 추가 */}
                </select>
              </div>
              
              {/* 🚨 [추가] 게시일시 추가 (마감일시 바로 전) */}
              <div>
                <label className="block mb-1">게시일시</label>
                <input type="datetime-local" value={newBid.ntce_date || ''} onChange={e => setNewBid({...newBid, ntce_date: e.target.value})} className="w-full p-2 border rounded outline-none focus:border-blue-500" />
              </div>

              <div>
                <label className="block mb-1">마감일시</label>
                <input type="datetime-local" value={newBid.end_date || ''} onChange={e => setNewBid({...newBid, end_date: e.target.value})} className="w-full p-2 border rounded outline-none focus:border-blue-500" />
              </div>
            </div>
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setIsAddModalOpen(false)} className="px-4 py-2 bg-slate-200 text-slate-700 font-bold rounded-lg hover:bg-slate-300">취소</button>
              <button onClick={handleAddBid} className="px-4 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700">저장하기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 🚨 [통합 수정] EditableCell: 드롭다운 및 텍스트 수정 통합 지원
function EditableCell({ bid, field, value, editingCell, setEditingCell, editValue, setEditValue, onSave, className = "", isSelect = false, options = [], placeholder = "" }: any) {
  const isEditing = editingCell?.bidNo === bid.bid_no && editingCell?.field === field;

  // 🚨 [디자인] 상태값에 따른 뱃지 스타일링
  const getStatusColor = (val: string) => {
    if (field !== 'status') return "";
    if (val === '낙찰완료') return "bg-blue-600 text-white px-2 py-0.5 rounded-full text-[10px]";
    if (val === '공고중') return "bg-emerald-500 text-white px-2 py-0.5 rounded-full text-[10px]";
    return "text-slate-600";
  };

  return (
    <td 
      className={`p-2 cursor-pointer group relative min-w-[60px] ${className} ${!value && !isEditing ? 'hover:bg-blue-50 transition-colors' : ''}`} 
      onClick={() => { 
        if(!isEditing) { 
          setEditingCell({bidNo: bid.bid_no, field}); 
          setEditValue(value || ""); 
        } 
      }}
    >
      <div className="flex items-center justify-between min-h-[1.2rem] w-full">
        {isEditing ? (
          <div className="flex items-center gap-1 w-full animate-in fade-in zoom-in duration-150">
            {isSelect ? (
              <select 
                autoFocus
                className="flex-1 border-2 border-blue-500 rounded px-1 py-0.5 bg-white text-slate-900 shadow-xl font-bold outline-none text-[10px]"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => setEditingCell(null)}
              >
                {options.map((opt: string) => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            ) : (
              <input 
                autoFocus 
                className="flex-1 border-2 border-blue-500 rounded px-2 py-0.5 bg-white text-slate-900 shadow-xl font-bold outline-none" 
                value={editValue} 
                onChange={(e) => setEditValue(e.target.value)} 
                onKeyDown={(e) => {
                   if (e.key === 'Enter') onSave(bid.bid_no, field);
                   if (e.key === 'Escape') setEditingCell(null);
                }}
                onBlur={() => setEditingCell(null)} 
              />
            )}
            {/* 🚨 확인 버튼 추가: 엔터 안쳐도 마우스로 클릭해서 저장 가능 */}
            <button 
              onMouseDown={(e) => { e.preventDefault(); onSave(bid.bid_no, field, editValue); }}
              className="p-1 bg-blue-600 text-white rounded shadow-md hover:bg-blue-700"
            >
              <Check size={12} strokeWidth={4} />
            </button>
          </div>
        ) : (
          <>
            <span className={`truncate ${getStatusColor(value)} ${!value ? 'text-slate-300 italic' : ''}`}>
              {value || placeholder || '-'}
            </span>
            <Edit3 size={8} className="text-blue-400 opacity-0 group-hover:opacity-100 shrink-0 ml-1 transition-opacity" />
          </>
        )}
      </div>
    </td>
  );
}